/**
 * Timeline data layer (Phase 1 #4).
 *
 * Pairs the typed `api` client with the central `queryKeys` factory. The feed
 * is page-based and consumed via `useInfiniteQuery`; mutations update the
 * infinite-query cache directly (optimistic like, prepend new post / comment).
 */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryKey,
} from '@tanstack/react-query';

import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/queryKeys';
import {
  uploadWithProgress,
  type UploadProgress,
} from '@/lib/uploadWithProgress';
import type {
  Activity,
  ActivityComment,
  Hashtag,
  Paginated,
  User,
} from '@/types/api';

/**
 * @deprecated Use `Activity` from `@/types/api`. Kept as an alias so existing
 * imports of `TimelineActivity` keep compiling.
 */
export type TimelineActivity = Activity;

export interface TimelineFeedParams {
  limit?: number;
  since?: string;
  type?: string;
  width?: number;
}

/** Default page size shared across the feed hooks (matches the API default). */
const PAGE_LIMIT = 10;

/** A single page of the feed: `{ list, total?, next? }`. */
type FeedPage = Paginated<Activity>;
type FeedInfinite = InfiniteData<FeedPage, number>;

function toQuery(params: Record<string, unknown> = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Compute the next page param. The server is the source of truth via
 * `next: boolean` — DON'T use `list.length < limit` as a stop signal.
 *
 * Why: `Activity.getTimelines` filters out rows with empty `data` (and
 * privacy-gated rows) AFTER the SQL `LIMIT`, so a page can legitimately
 * come back with fewer than `limit` items while more pages still exist.
 * The server sets `next: items.length > 0` based on the PRE-filter count,
 * which correctly reflects "another page is available". A `got < limit`
 * client heuristic stopped pagination early and capped the dashboard
 * feed at one short page on accounts where empty-data rows are common.
 */
function nextPageParam(
  lastPage: FeedPage,
  _all: FeedPage[],
  lastParam: number,
  _limit: number,
): number | undefined {
  if (lastPage.next === false) return undefined;
  if ((lastPage.list?.length ?? 0) === 0) return undefined;
  return lastParam + 1;
}

/* -------------------------------------------------------------------------- */
/* Feeds                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Infinite activity feed for a user (`GET /timeline/list/{id}`).
 * Pass `0` (default) for the authenticated user's main feed; a user id for a
 * profile feed.
 */
export function useTimelineFeed(
  userId: number | string = 0,
  params: TimelineFeedParams = {},
) {
  const limit = params.limit ?? PAGE_LIMIT;
  const width = params.width ?? 600;
  return useInfiniteQuery<
    FeedPage,
    Error,
    FeedInfinite,
    QueryKey,
    number
  >({
    queryKey: queryKeys.timeline.list(userId, { ...params, limit, width }),
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      api.get<FeedPage>(
        `timeline/list/${userId}${toQuery({ ...params, page: pageParam, limit, width })}`,
      ),
    getNextPageParam: (lastPage, all, lastParam) =>
      nextPageParam(lastPage, all, lastParam, limit),
  });
}

/**
 * Public homepage showcase — latest publicly-visible Blog / Photo / Video
 * activities. Public route (`middleware: false`), so it works for anonymous
 * marketing-site visitors. Returns at most `limit` rows.
 */
export interface PublicShowcaseItem {
  ID: number;
  /** `'B'` (blog), `'P'` (photo album), `'V'` (video). */
  object_type: 'B' | 'P' | 'V';
  datetime: string;
  title?: string | null;
  is_video: boolean;
  /** For video activities, the source's width/height ratio (e.g. 16/9 ≈
   *  1.7778 for YouTube/Vimeo/Dailymotion + sensible default for mp4).
   *  `null` for blog/photo tiles (those use the masonry's variable height
   *  classes instead). */
  aspect_ratio: number | null;
  image: string;
  /** Real like count from `::likes`. The home-page tile hides the heart
   *  badge entirely when this is 0. */
  likes: number;
  /** Blog-only: slug + category slug so the homepage tile can deep-link
   *  directly to `/blogs/{category_slug}/{slug}` instead of the author's
   *  activity-detail page. `null` for photo/video tiles. */
  blog?: { slug: string; category_slug: string } | null;
  user: {
    ID: number | string;
    username?: string;
    firstname?: string;
    lastname?: string;
    /** Resolved profile photo URL (sized variant); null when the user has
     *  no avatar — the tile falls back to initials. */
    image?: string | null;
  };
}

export function usePublicShowcase(limit = 12) {
  return useQuery<{ list: PublicShowcaseItem[] }>({
    queryKey: queryKeys.timeline.publicShowcase({ limit }),
    queryFn: () =>
      api.get<{ list: PublicShowcaseItem[] }>(
        `timeline/public-showcase?limit=${limit}`,
      ),
    // Showcase content drifts slowly — match the backend cache TTL (60s)
    // so we don't re-request when remounted within the same browse session.
    staleTime: 60_000,
  });
}

/** Infinite feed for a hashtag (`GET /timeline/hashtag/{tag}`). */
export function useHashtagFeed(tag: string, params: TimelineFeedParams = {}) {
  const limit = params.limit ?? PAGE_LIMIT;
  const width = params.width ?? 600;
  return useInfiniteQuery<
    FeedPage,
    Error,
    FeedInfinite,
    QueryKey,
    number
  >({
    queryKey: queryKeys.timeline.hashtag(tag, { ...params, limit, width }),
    enabled: !!tag,
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      api.get<FeedPage>(
        `timeline/hashtag/${encodeURIComponent(tag)}${toQuery({ ...params, page: pageParam, limit, width })}`,
      ),
    getNextPageParam: (lastPage, all, lastParam) =>
      nextPageParam(lastPage, all, lastParam, limit),
  });
}

/** Trending hashtags (`GET /timeline/hashtag/list`). */
export function useTrendingHashtags(limit = 8) {
  return useQuery({
    queryKey: queryKeys.timeline.trending({ limit }),
    queryFn: () =>
      api.get<Paginated<Hashtag>>(`timeline/hashtag/list${toQuery({ limit })}`),
  });
}

/* -------------------------------------------------------------------------- */
/* Cache helpers                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Run `mutate` against every cached feed page (main feed, profile feeds,
 * hashtag feeds) that contains the activity. Returns the affected query keys +
 * their previous data so the caller can roll back on error.
 */
function patchActivityInFeeds(
  qc: ReturnType<typeof useQueryClient>,
  activityId: number,
  mutate: (a: Activity) => Activity,
): Array<[QueryKey, unknown]> {
  const snapshots: Array<[QueryKey, unknown]> = [];
  const queries = qc.getQueriesData<unknown>({
    queryKey: queryKeys.timeline.all,
  });
  for (const [key, data] of queries) {
    if (!data) continue;

    // Single-activity detail cache (`['timeline', 'detail', id]`) — backs
    // the `/user/:u/activity/id/:id` deep link via `useActivity`. Holds a
    // flat `Activity | null`, NOT an InfiniteData wrapper. Patch directly.
    if (Array.isArray(key) && key[1] === 'detail') {
      const activity = data as Activity | null;
      if (activity && activity.ID === activityId) {
        snapshots.push([key, activity]);
        qc.setQueryData<Activity>(key, mutate(activity));
      }
      continue;
    }

    // Comments / likes infinite caches share the `['timeline', …]`
    // prefix but their row shape isn't `Activity`; skip them so the
    // mutator doesn't walk over them with garbled data.
    if (Array.isArray(key) && (key[1] === 'comments' || key[1] === 'likes')) {
      continue;
    }

    // Feed cache: `InfiniteData<{ list: Activity[] }>`. Patch the matching
    // row inside each page.
    const feed = data as FeedInfinite;
    if (!feed.pages) continue;
    let touched = false;
    const pages = feed.pages.map((page) => {
      const list = page.list ?? [];
      if (!list.some((a) => a.ID === activityId)) return page;
      touched = true;
      return {
        ...page,
        list: list.map((a) => (a.ID === activityId ? mutate(a) : a)),
      };
    });
    if (touched) {
      snapshots.push([key, feed]);
      qc.setQueryData<FeedInfinite>(key, { ...feed, pages });
    }
  }
  return snapshots;
}

/* -------------------------------------------------------------------------- */
/* Action mutations                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Like / unlike an activity with an optimistic toggle of `liked` + `likes`
 * across every cached feed. Rolls back on error.
 */
export function useLike() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, liked }: { id: number; liked: boolean }) =>
      api.post(`timeline/${id}/action/${liked ? 'unlike' : 'like'}`),
    onMutate: async ({ id, liked }) => {
      // Cancel only feed-shaped infinites — the bare `timeline.all`
      // prefix also matched comments / likes infinites and threw
      // their pollers off-cycle for every like click.
      await qc.cancelQueries({
        predicate: (q) => {
          const k = q.queryKey;
          if (!Array.isArray(k) || k[0] !== 'timeline') return false;
          // Skip per-activity polls (comments / likes / items)
          return k[1] !== 'items' && k[1] !== 'detail';
        },
      });
      const snapshots = patchActivityInFeeds(qc, id, (a) => ({
        ...a,
        liked: !liked,
        likes: Math.max(0, a.likes + (liked ? -1 : 1)),
      }));
      return { snapshots };
    },
    onError: (_err, _vars, ctx) => {
      ctx?.snapshots.forEach(([key, data]) => qc.setQueryData(key, data));
    },
  });
}

/** Add a comment to an activity (optimistic count bump + lastComment). */
export function useAddComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: number; comment: string }) =>
      api.post<ActivityComment>(`timeline/${id}/action/comment`, { comment }),
    onSuccess: (data, { id }) => {
      patchActivityInFeeds(qc, id, (a) => ({
        ...a,
        comments: a.comments + 1,
        lastComment: data ?? a.lastComment,
      }));
      qc.invalidateQueries({ queryKey: queryKeys.timeline.items(id, 'comments') });
    },
  });
}

/** Delete a comment from an activity (decrements the count). */
export function useDeleteComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, cid }: { id: number; cid: number }) =>
      api.post(`timeline/${id}/action/delcomment`, { cid }),
    onSuccess: (_data, { id }) => {
      patchActivityInFeeds(qc, id, (a) => ({
        ...a,
        comments: Math.max(0, a.comments - 1),
      }));
      qc.invalidateQueries({ queryKey: queryKeys.timeline.items(id, 'comments') });
    },
  });
}

/** Delete an activity and remove it from every cached feed. */
export function useDeleteActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`timeline/${id}/action/delete`),
    onSuccess: (_data, id) => {
      // Walk infinite feeds only (not comments / likes / detail /
      // showcase — those have different data shapes handled below).
      const queries = qc.getQueriesData<FeedInfinite>({
        predicate: (q) => {
          const k = q.queryKey;
          if (!Array.isArray(k) || k[0] !== 'timeline') return false;
          return k[1] !== 'items' && k[1] !== 'detail';
        },
      });
      for (const [key, data] of queries) {
        if (!data?.pages) continue;
        qc.setQueryData<FeedInfinite>(key, {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            list: (page.list ?? []).filter((a) => a.ID !== id),
          })),
        });
      }

      // Public homepage showcase — a single-page useQuery, NOT infinite,
      // so the loop above skipped it (`data.pages` is undefined). Update
      // in-place for instant visual feedback AND invalidate so any
      // remounted consumer refetches from the backend (which already
      // busted its 60 s cache via CK.invalidatePublicShowcase in the
      // TimelineController delete branch).
      const showcaseQueries = qc.getQueriesData<{ list: Array<{ ID: number }> }>({
        predicate: (q) => {
          const k = q.queryKey;
          return Array.isArray(k) && k[0] === 'timeline' && k[1] === 'public-showcase';
        },
      });
      // eslint-disable-next-line no-console
      console.log('[useDeleteActivity] id=', id, 'showcaseQueries.length=', showcaseQueries.length,
        showcaseQueries.map(([k, d]) => ({ key: k, itemCount: d?.list?.length ?? 'no-list', includedId: !!d?.list?.some((x) => x.ID === id) })));
      for (const [key, data] of showcaseQueries) {
        if (!data || !Array.isArray(data.list)) continue;
        qc.setQueryData(key, {
          ...data,
          list: data.list.filter((a) => a.ID !== id),
        });
      }
      qc.invalidateQueries({
        predicate: (q) => {
          const k = q.queryKey;
          return Array.isArray(k) && k[0] === 'timeline' && k[1] === 'public-showcase';
        },
      });
      // Also invalidate the /trending feed — same shape as showcase and
      // often driven by the same underlying activities set.
      qc.invalidateQueries({
        predicate: (q) => {
          const k = q.queryKey;
          return Array.isArray(k) && k[0] === 'timeline' && k[1] === 'trending';
        },
      });

      // Single-activity detail (`useActivity`) → drop so deep links
      // resolve to a 404 instead of serving the stale payload.
      qc.removeQueries({ queryKey: queryKeys.timeline.detail(id) });
    },
  });
}

/** Report an activity. */
export function useReportActivity() {
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: number }) =>
      api.post(`timeline/${id}/action/report`, { reason }),
  });
}

/* -------------------------------------------------------------------------- */
/* Comments (lazy / infinite)                                                  */
/* -------------------------------------------------------------------------- */

type CommentsPage = Paginated<ActivityComment>;
type CommentsInfinite = InfiniteData<CommentsPage, number>;

/* -------------------------------------------------------------------------- */
/* Likes (see-who-liked-it modal)                                              */
/* -------------------------------------------------------------------------- */

/** Single row returned by `/timeline/:id/items/likes` — a like + actor. */
export interface ActivityLike {
  ID: number;
  time?: string;
  byme?: boolean;
  user?: User | null;
}

interface LikesPage {
  total: number;
  list: ActivityLike[];
  next: boolean;
}

type LikesInfinite = InfiniteData<LikesPage, number>;

/**
 * Lazily-loaded paginated list of users who liked an activity
 * (`GET /timeline/{id}/items/likes`). The backend treats `:type` as a free
 * string — anything other than `'comments'` queries the `::likes` table —
 * so we just call the canonical `'likes'` path. Disabled until `enabled`
 * (i.e. the modal opens) so we don't fetch for every post in the feed.
 */
export function useLikedBy(
  activityId: number,
  enabled = false,
  limit = 30,
) {
  return useInfiniteQuery<
    LikesPage,
    Error,
    LikesInfinite,
    QueryKey,
    number
  >({
    queryKey: queryKeys.timeline.items(activityId, 'likes'),
    enabled,
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      api.get<LikesPage>(
        `timeline/${activityId}/items/likes${toQuery({ page: pageParam, limit })}`,
      ),
    getNextPageParam: (lastPage, _all, lastParam) =>
      lastPage.next === false ? undefined : lastParam + 1,
  });
}

/**
 * Lazily-loaded, paginated comments for an activity
 * (`GET /timeline/{id}/items/comments`). Disabled until `enabled` is set
 * (e.g. when the comment section is expanded).
 */
export function useComments(activityId: number, enabled = false, limit = 5) {
  return useInfiniteQuery<
    CommentsPage,
    Error,
    CommentsInfinite,
    QueryKey,
    number
  >({
    queryKey: queryKeys.timeline.items(activityId, 'comments'),
    enabled,
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      api.get<CommentsPage>(
        `timeline/${activityId}/items/comments${toQuery({ page: pageParam, limit })}`,
      ),
    getNextPageParam: (lastPage, _all, lastParam) =>
      lastPage.next === false ? undefined : lastParam + 1,
  });
}

/* -------------------------------------------------------------------------- */
/* Create post                                                                 */
/* -------------------------------------------------------------------------- */

export type CreatePostType = 'status' | 'photo' | 'video' | 'blog';

export interface CreateStatusInput {
  type: 'status';
  description: string;
  location?: string;
  privacy?: number;
  extra?: Record<string, unknown>;
}
export interface CreatePhotoInput {
  type: 'photo';
  photos: File[];
  description?: string;
  privacy?: number;
}
export interface CreateVideoInput {
  type: 'video';
  video_url?: string;
  video?: File;
  description?: string;
  privacy?: number;
}
export interface CreateBlogInput {
  type: 'blog';
  title: string;
  description: string;
  category_id: number | string;
  cover?: File;
  privacy?: number;
  /**
   * When set, edits an existing blog (`POST /timeline/post/blog/id/:id`)
   * instead of creating a new one.
   */
  id?: number | string;
  /**
   * On edit, the URL of the currently-stored cover image. If the user did
   * not pick a new `cover` file, this is sent as `image` in the body so the
   * backend preserves the existing cover (otherwise it would null it out —
   * see TimelineController.post line ~992: `if (!req.body.image)
   * blogData.image = null;`).
   */
  existingImage?: string;
}

export type CreatePostInput =
  | CreateStatusInput
  | CreatePhotoInput
  | CreateVideoInput
  | CreateBlogInput;

/** Response envelope for `POST /timeline/post/{type}`. */
interface CreatePostResult {
  msg?: string;
  data: Activity;
}

/** Build the request body (JSON object or FormData) for a create-post call. */
function buildPostBody(input: CreatePostInput): unknown {
  switch (input.type) {
    case 'status':
      return {
        description: input.description,
        ...(input.location ? { location: input.location } : {}),
        ...(input.extra ? { extra: input.extra } : {}),
        ...(input.privacy != null ? { privacy: input.privacy } : {}),
      };
    case 'photo': {
      const fd = new FormData();
      input.photos.forEach((f) => fd.append('photo[]', f));
      if (input.description) fd.append('description', input.description);
      if (input.privacy != null) fd.append('privacy', String(input.privacy));
      return fd;
    }
    case 'video': {
      if (input.video) {
        const fd = new FormData();
        fd.append('video', input.video);
        if (input.description) fd.append('description', input.description);
        if (input.privacy != null) fd.append('privacy', String(input.privacy));
        return fd;
      }
      return {
        video_url: input.video_url,
        ...(input.description ? { description: input.description } : {}),
        ...(input.privacy != null ? { privacy: input.privacy } : {}),
      };
    }
    case 'blog': {
      if (input.cover) {
        const fd = new FormData();
        fd.append('title', input.title);
        fd.append('description', input.description);
        fd.append('category_id', String(input.category_id));
        fd.append('photo', input.cover);
        if (input.privacy != null) fd.append('privacy', String(input.privacy));
        // On EDIT with a new cover, the file replaces the old one — no
        // `image` field needed; backend's photo branch will overwrite.
        return fd;
      }
      return {
        title: input.title,
        description: input.description,
        category_id: input.category_id,
        ...(input.privacy != null ? { privacy: input.privacy } : {}),
        // On EDIT without a new cover, echo the existing URL so the
        // backend doesn't null out the column (see CreateBlogInput.docs).
        ...(input.id && input.existingImage
          ? { image: input.existingImage }
          : {}),
      };
    }
  }
}

/**
 * Mutation variables for `useCreatePost`. Wraps the typed `CreatePostInput`
 * with an optional `onUploadProgress` callback that fires while a multipart
 * body is streaming out (photo / video-file / blog-cover posts only — JSON
 * posts have no meaningful upload progress).
 */
export interface CreatePostVars {
  input: CreatePostInput;
  onUploadProgress?: (progress: UploadProgress) => void;
}

/** True when the request body for this input is FormData (multipart). */
function isMultipartInput(input: CreatePostInput): boolean {
  if (input.type === 'photo') return true;
  if (input.type === 'video' && input.video) return true;
  if (input.type === 'blog' && input.cover) return true;
  return false;
}

/**
 * Create a post of any type (`POST /timeline/post/{type}`). Multipart is used
 * for photo/file uploads automatically (the `api` client handles FormData).
 * On success the returned activity is prepended to the main feed cache.
 *
 * Accepts either `CreatePostInput` directly (legacy / JSON posts) or a
 * `{ input, onUploadProgress }` object — the latter routes multipart bodies
 * through `uploadWithProgress` so callers (the Composer) can show a real
 * upload percentage in the Post button.
 */
export function useCreatePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: CreatePostInput | CreatePostVars) => {
      const isVars = 'input' in vars;
      const input: CreatePostInput = isVars
        ? (vars as CreatePostVars).input
        : (vars as CreatePostInput);
      const onUploadProgress = isVars
        ? (vars as CreatePostVars).onUploadProgress
        : undefined;

      // Edits route to `timeline/post/{type}/id/{id}` (see Node
      // `config/apis.js` line 82). Same controller handler — `editId` is
      // read from `req.params.id`.
      const editId =
        input.type === 'blog' && input.id != null ? input.id : null;
      const url = editId
        ? `timeline/post/${input.type}/id/${editId}`
        : `timeline/post/${input.type}`;

      const body = buildPostBody(input);

      // Multipart bodies route through XHR so we can surface upload progress.
      // JSON bodies stay on the fetch-based `api.post` path.
      if (body instanceof FormData && isMultipartInput(input)) {
        return uploadWithProgress<CreatePostResult>({
          url,
          body,
          onProgress: onUploadProgress,
        });
      }
      return api.post<CreatePostResult>(url, body);
    },
    onSuccess: (_res, vars) => {
      const input: CreatePostInput =
        'input' in vars ? (vars as CreatePostVars).input : (vars as CreatePostInput);
      // Invalidate the timeline feed so the next render pulls the
      // canonical activity shape from `getTimelines` (which has the
      // resolved `user`, parsed photos, share_url, uppercase `type`,
      // etc.). The POST handler returns a SIMPLIFIED activity shape —
      // prepending it directly produced empty cards (header only, no
      // body) until the user refreshed. The server-side `CK.invalidate-
      // TimelineGlobal()` runs in the POST handler so the next fetch
      // sees fresh data.
      const isEdit = input.type === 'blog' && input.id != null;
      if (isEdit) return;
      // Invalidate feed-shaped caches only — the broader
      // `timeline.all` prefix also matched comments / likes / detail
      // entries and triggered unnecessary refetches on every post.
      qc.invalidateQueries({
        predicate: (q) => {
          const k = q.queryKey;
          if (!Array.isArray(k) || k[0] !== 'timeline') return false;
          return k[1] !== 'items' && k[1] !== 'detail';
        },
      });
    },
  });
}

/* -------------------------------------------------------------------------- */
/* AI generation                                                               */
/* -------------------------------------------------------------------------- */

/** Generate AI content (`POST /timeline/generate-ai`). */
export function useGenerateAI() {
  return useMutation({
    mutationFn: ({ prompt, type = 'status' }: { prompt: string; type?: 'status' | 'blog' }) =>
      api.post<{ text: string }>('timeline/generate-ai', { prompt, type }),
  });
}
